/**
 * Approval Workflows Admin Page
 * Manage approval chains, requests, and pending approvals
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Plus,
  Trash2,
  Loader2,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronRight,
  Link2,
  GitBranch,
  ClipboardList,
  Clock,
} from 'lucide-react';

// --- Types ---

interface ApprovalChain {
  id: string;
  name: string;
  entityType: string;
  steps: ApprovalStep[];
  isActive: boolean;
  createdAt: string;
}

interface ApprovalStep {
  id?: string;
  stepOrder: number;
  approverId: string;
}

interface ApprovalRequest {
  id: string;
  chainId: string;
  chainName: string;
  entityType: string;
  entityId: string;
  requestorId: string;
  requestorName: string;
  status: string;
  currentStep: number;
  actions: ApprovalAction[];
  createdAt: string;
}

interface ApprovalAction {
  id: string;
  stepOrder: number;
  approverId: string;
  approverName: string;
  action: string;
  comment: string | null;
  createdAt: string;
}

// --- Constants ---

const ENTITY_TYPES = ['ticket', 'document', 'invoice', 'contract', 'expense'];

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-blue-100 text-blue-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-800',
};

const TAB_KEYS = ['chains', 'requests', 'pending'] as const;
type TabKey = (typeof TAB_KEYS)[number];

// --- Component ---

export function ApprovalsPage() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabKey>('chains');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Approval Workflows</h1>
        <p className="text-muted-foreground">
          Manage approval chains, review requests, and act on pending approvals
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {[
          { key: 'chains' as TabKey, label: 'Chains', icon: Link2 },
          { key: 'requests' as TabKey, label: 'Requests', icon: ClipboardList },
          { key: 'pending' as TabKey, label: 'My Pending', icon: Clock },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === key
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'chains' && <ChainsTab queryClient={queryClient} />}
      {activeTab === 'requests' && <RequestsTab />}
      {activeTab === 'pending' && <PendingTab queryClient={queryClient} />}
    </div>
  );
}

// --- Chains Tab ---

function ChainsTab({ queryClient }: { queryClient: ReturnType<typeof useQueryClient> }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [chainName, setChainName] = useState('');
  const [entityType, setEntityType] = useState('');
  const [steps, setSteps] = useState<ApprovalStep[]>([{ stepOrder: 1, approverId: '' }]);

  const { data, isLoading } = useQuery({
    queryKey: ['approvalChains'],
    queryFn: () => api.getApprovalChains(),
  });
  const chains = (data?.data || []) as ApprovalChain[];

  const createMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) => api.createApprovalChain(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['approvalChains'] });
      setDialogOpen(false);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteApprovalChain(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['approvalChains'] });
    },
  });

  function resetForm() {
    setChainName('');
    setEntityType('');
    setSteps([{ stepOrder: 1, approverId: '' }]);
  }

  function addStep() {
    setSteps((prev) => [...prev, { stepOrder: prev.length + 1, approverId: '' }]);
  }

  function removeStep(index: number) {
    setSteps((prev) =>
      prev
        .filter((_, i) => i !== index)
        .map((s, i) => ({ ...s, stepOrder: i + 1 }))
    );
  }

  function updateStepApprover(index: number, approverId: string) {
    setSteps((prev) =>
      prev.map((s, i) => (i === index ? { ...s, approverId } : s))
    );
  }

  function handleCreateChain() {
    createMutation.mutate({
      name: chainName,
      entityType,
      steps: steps.map((s) => ({ stepOrder: s.stepOrder, approverId: s.approverId })),
    });
  }

  return (
    <>
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Approval Chains</h2>
        <Button onClick={() => setDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Chain
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : chains.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <GitBranch className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No approval chains configured yet</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Entity Type</TableHead>
                  <TableHead className="text-center">Steps</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {chains.map((chain) => (
                  <TableRow key={chain.id}>
                    <TableCell className="font-medium">{chain.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {chain.entityType}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      {chain.steps?.length || 0}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant={chain.isActive ? 'default' : 'secondary'}>
                        {chain.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (confirm('Delete this approval chain?'))
                            deleteMutation.mutate(chain.id);
                        }}
                      >
                        <Trash2 className="h-3 w-3 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* New Chain Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>New Approval Chain</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Chain Name</Label>
              <Input
                value={chainName}
                onChange={(e) => setChainName(e.target.value)}
                placeholder="e.g., Invoice Approval"
              />
            </div>
            <div>
              <Label>Entity Type</Label>
              <Select value={entityType} onValueChange={setEntityType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select entity type" />
                </SelectTrigger>
                <SelectContent>
                  {ENTITY_TYPES.map((type) => (
                    <SelectItem key={type} value={type} className="capitalize">
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Approval Steps</Label>
              <div className="space-y-2 mt-2">
                {steps.map((step, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground w-8">
                      #{step.stepOrder}
                    </span>
                    <Input
                      placeholder="Approver ID"
                      value={step.approverId}
                      onChange={(e) => updateStepApprover(index, e.target.value)}
                      className="flex-1"
                    />
                    {steps.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeStep(index)}
                      >
                        <Trash2 className="h-3 w-3 text-destructive" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addStep}>
                  <Plus className="h-3 w-3 mr-1" />
                  Add Step
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateChain}
              disabled={!chainName || !entityType || createMutation.isPending}
            >
              {createMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// --- Requests Tab ---

function RequestsTab() {
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['approvalRequests', { status: statusFilter }],
    queryFn: () =>
      api.getApprovalRequests({
        status: statusFilter || undefined,
        limit: 50,
      }),
  });
  const requests = (data?.data || []) as ApprovalRequest[];

  function toggleExpanded(id: string) {
    setExpandedId((prev) => (prev === id ? null : id));
  }

  return (
    <>
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Approval Requests</h2>
        <Select
          value={statusFilter || 'all'}
          onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : requests.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ClipboardList className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No approval requests found</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead>Chain</TableHead>
                  <TableHead>Entity Type</TableHead>
                  <TableHead>Entity ID</TableHead>
                  <TableHead>Requestor</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-center">Current Step</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requests.map((req) => (
                  <>
                    <TableRow
                      key={req.id}
                      className="cursor-pointer"
                      onClick={() => toggleExpanded(req.id)}
                    >
                      <TableCell>
                        {expandedId === req.id ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </TableCell>
                      <TableCell className="font-medium">
                        {req.chainName}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {req.entityType}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {req.entityId?.slice(0, 8)}...
                      </TableCell>
                      <TableCell>{req.requestorName || req.requestorId}</TableCell>
                      <TableCell className="text-center">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            STATUS_COLORS[req.status] || 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {req.status}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        {req.currentStep}
                      </TableCell>
                    </TableRow>
                    {expandedId === req.id && (
                      <TableRow key={`${req.id}-actions`}>
                        <TableCell colSpan={7} className="bg-muted/30 p-4">
                          <h4 className="text-sm font-semibold mb-2">
                            Action History
                          </h4>
                          {req.actions && req.actions.length > 0 ? (
                            <div className="space-y-2">
                              {req.actions.map((action) => (
                                <div
                                  key={action.id}
                                  className="flex items-start gap-3 text-sm"
                                >
                                  {action.action === 'approve' ? (
                                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
                                  ) : (
                                    <XCircle className="h-4 w-4 text-red-500 mt-0.5" />
                                  )}
                                  <div>
                                    <span className="font-medium">
                                      {action.approverName || action.approverId}
                                    </span>
                                    <span className="text-muted-foreground">
                                      {' '}
                                      {action.action} at step {action.stepOrder}
                                    </span>
                                    {action.comment && (
                                      <p className="text-muted-foreground mt-1">
                                        &quot;{action.comment}&quot;
                                      </p>
                                    )}
                                    <p className="text-xs text-muted-foreground">
                                      {new Date(action.createdAt).toLocaleString()}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground">
                              No actions recorded yet
                            </p>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </>
  );
}

// --- My Pending Tab ---

function PendingTab({ queryClient }: { queryClient: ReturnType<typeof useQueryClient> }) {
  const [comments, setComments] = useState<Record<string, string>>({});

  const { data, isLoading } = useQuery({
    queryKey: ['myPendingApprovals'],
    queryFn: () => api.getMyPendingApprovals(),
  });
  const pending = (data?.data || []) as ApprovalRequest[];

  const actionMutation = useMutation({
    mutationFn: ({ id, action, comment }: { id: string; action: string; comment?: string }) =>
      api.actOnApprovalRequest(id, { action, comment }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myPendingApprovals'] });
      queryClient.invalidateQueries({ queryKey: ['approvalRequests'] });
    },
  });

  function handleAction(requestId: string, action: string) {
    actionMutation.mutate({
      id: requestId,
      action,
      comment: comments[requestId] || undefined,
    });
  }

  return (
    <>
      <h2 className="text-lg font-semibold">My Pending Approvals</h2>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : pending.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CheckCircle className="h-12 w-12 text-green-500 mb-4" />
            <p className="text-muted-foreground">No pending approvals</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {pending.map((req) => (
            <Card key={req.id}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">{req.chainName}</h3>
                  <Badge variant="outline" className="capitalize">
                    {req.entityType}
                  </Badge>
                </div>
                <div className="text-sm space-y-1">
                  <p>
                    <span className="text-muted-foreground">Entity ID: </span>
                    <span className="font-mono text-xs">{req.entityId?.slice(0, 12)}...</span>
                  </p>
                  <p>
                    <span className="text-muted-foreground">Requestor: </span>
                    {req.requestorName || req.requestorId}
                  </p>
                  <p>
                    <span className="text-muted-foreground">Step: </span>
                    {req.currentStep}
                  </p>
                </div>
                <Textarea
                  placeholder="Optional comment..."
                  rows={2}
                  value={comments[req.id] || ''}
                  onChange={(e) =>
                    setComments((prev) => ({ ...prev, [req.id]: e.target.value }))
                  }
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="flex-1"
                    onClick={() => handleAction(req.id, 'approve')}
                    disabled={actionMutation.isPending}
                  >
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    className="flex-1"
                    onClick={() => handleAction(req.id, 'reject')}
                    disabled={actionMutation.isPending}
                  >
                    <XCircle className="h-4 w-4 mr-1" />
                    Reject
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}
